import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SystemUpdate as FirmwareIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  DevicesOther as DeviceIcon,
  Route as PathIcon,
  NewReleases as LatestIcon,
  MenuBook as DocIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import { firmwareVersionsApi, updatePathsApi, devicesApi, documentationApi } from '../services/apiClient';
import { usePermissions } from '../contexts/PermissionContext';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`firmware-tabpanel-${index}`}
      aria-labelledby={`firmware-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const FirmwareManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Data state
  const [firmwareVersions, setFirmwareVersions] = useState([]);
  const [updatePaths, setUpdatePaths] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [firmwareDialogOpen, setFirmwareDialogOpen] = useState(false);
  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingFirmware, setEditingFirmware] = useState(null);
  const [editingPath, setEditingPath] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state for firmware
  const [firmwareForm, setFirmwareForm] = useState({
    version: '',
    path: '',
    device_type: '',
    isLatest: false,
    downloadUrl: ''
  });

  // Form state for update path
  const [pathForm, setPathForm] = useState({
    update_path_device_type: '',
    actual_version: '',
    canSkip: false
  });

  // Documentation dialog state
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docContent, setDocContent] = useState('');
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(null);
  const [docTitle, setDocTitle] = useState('');

  // Check permissions
  const canManage = hasPermission('admin') || hasPermission('devices.edit');

  // Map device type friendlyName to deviceScript folder
  const getDeviceScriptFolder = (deviceType) => {
    const name = deviceType?.friendlyName?.toLowerCase() || '';
    if (name.includes('hub')) return 'caro_hub';
    if (name.includes('agent')) return 'caro_agent';
    if (name.includes('duckbill')) return 'caro_antenna_duckbill';
    if (name.includes('raspi') && name.includes('zero')) return 'caro_antenna_raspi_zero';
    if (name.includes('raspi') && (name.includes('b') || name.includes('3'))) return 'caro_antenna_raspi_B';
    return null;
  };

  // Handle opening documentation dialog
  const handleOpenDocDialog = async () => {
    const selectedDT = deviceTypes.find(dt => dt.id === selectedDeviceType);
    const folder = getDeviceScriptFolder(selectedDT);
    if (!folder) {
      enqueueSnackbar('No documentation folder mapped for this device type', { variant: 'warning' });
      return;
    }

    setDocTitle(selectedDT?.friendlyName || folder);
    setDocDialogOpen(true);
    setDocLoading(true);
    setDocError(null);
    setDocContent('');

    try {
      const response = await documentationApi.getDeviceScriptDoc(folder);
      const content = response.data?.doc?.content || response.data?.data?.content || response.data?.content || '';
      if (typeof content === 'string' && content.trim()) {
        setDocContent(content);
      } else {
        setDocError('No documentation available for this device type.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setDocError('No documentation available for this device type.');
      } else {
        setDocError('Failed to load documentation.');
      }
    } finally {
      setDocLoading(false);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fwResponse, pathResponse, dtResponse] = await Promise.all([
        firmwareVersionsApi.getAll({ limit: 1000 }),
        updatePathsApi.getAll({ limit: 1000 }),
        devicesApi.getDeviceTypes({ limit: 100 })
      ]);

      if (fwResponse.data.success) {
        setFirmwareVersions(fwResponse.data.data || []);
      }
      if (pathResponse.data.success) {
        setUpdatePaths(pathResponse.data.data || []);
      }
      if (dtResponse.data.success || dtResponse.data.data) {
        // Filter to only show device types that have firmware (those in deviceScripts)
        const types = dtResponse.data.data || [];
        const firmwareDeviceTypes = types.filter(dt =>
          dt.friendlyName?.toLowerCase().includes('hub') ||
          dt.friendlyName?.toLowerCase().includes('antenna') ||
          dt.friendlyName?.toLowerCase().includes('agent') ||
          dt.friendlyName?.toLowerCase().includes('raspi') ||
          dt.friendlyName?.toLowerCase().includes('duckbill')
        );
        setDeviceTypes(firmwareDeviceTypes.length > 0 ? firmwareDeviceTypes : types);
      }
    } catch (error) {
      console.error('Error fetching firmware data:', error);
      enqueueSnackbar('Failed to load firmware data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get device type name
  const getDeviceTypeName = (deviceTypeId) => {
    const dt = deviceTypes.find(d => d.id === deviceTypeId);
    return dt?.friendlyName || `Type ${deviceTypeId}`;
  };

  // Get firmware version display - version numbers are stored directly, 0 means "latest"
  const getFirmwareVersionDisplay = (versionNumber) => {
    if (versionNumber === 0) return 'Latest (none)';
    return `v${versionNumber}`;
  };

  // Filter data by device type
  const filteredFirmware = selectedDeviceType
    ? firmwareVersions.filter(fw => fw.device_type === selectedDeviceType)
    : firmwareVersions;

  const filteredPaths = selectedDeviceType
    ? updatePaths.filter(p => p.update_path_device_type === selectedDeviceType)
    : updatePaths;

  // Handle firmware dialog
  const handleOpenFirmwareDialog = (firmware = null) => {
    if (firmware) {
      setEditingFirmware(firmware);
      setFirmwareForm({
        version: firmware.version?.toString() || '',
        path: firmware.path || '',
        device_type: firmware.device_type || '',
        isLatest: firmware.isLatest || false,
        downloadUrl: firmware.downloadUrl || ''
      });
    } else {
      setEditingFirmware(null);
      setFirmwareForm({
        version: '',
        path: '',
        device_type: selectedDeviceType || '',
        isLatest: false,
        downloadUrl: ''
      });
    }
    setFirmwareDialogOpen(true);
  };

  const handleCloseFirmwareDialog = () => {
    setFirmwareDialogOpen(false);
    setEditingFirmware(null);
  };

  const handleSaveFirmware = async () => {
    if (!firmwareForm.version || !firmwareForm.path || !firmwareForm.device_type) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const data = {
        version: parseInt(firmwareForm.version, 10),
        path: firmwareForm.path,
        device_type: firmwareForm.device_type,
        isLatest: firmwareForm.isLatest,
        downloadUrl: firmwareForm.downloadUrl
      };

      if (editingFirmware) {
        await firmwareVersionsApi.update(editingFirmware.id, data);
        enqueueSnackbar('Firmware version updated', { variant: 'success' });
      } else {
        await firmwareVersionsApi.create(data);
        enqueueSnackbar('Firmware version created', { variant: 'success' });
      }

      handleCloseFirmwareDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving firmware:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save firmware version', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle path dialog
  const handleOpenPathDialog = (path = null) => {
    if (path) {
      setEditingPath(path);
      setPathForm({
        update_path_device_type: path.update_path_device_type || '',
        actual_version: path.actual_version || '',
        canSkip: path.canSkip || false
      });
    } else {
      setEditingPath(null);
      setPathForm({
        update_path_device_type: selectedDeviceType || '',
        actual_version: '',
        canSkip: false
      });
    }
    setPathDialogOpen(true);
  };

  const handleClosePathDialog = () => {
    setPathDialogOpen(false);
    setEditingPath(null);
  };

  const handleSavePath = async () => {
    if (!pathForm.update_path_device_type || pathForm.actual_version === '') {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const data = {
        update_path_device_type: pathForm.update_path_device_type,
        actual_version: parseInt(pathForm.actual_version, 10),
        canSkip: pathForm.canSkip
      };

      if (editingPath) {
        await updatePathsApi.update(editingPath.id, data);
        enqueueSnackbar('Update path updated', { variant: 'success' });
      } else {
        await updatePathsApi.create(data);
        enqueueSnackbar('Update path created', { variant: 'success' });
      }

      handleClosePathDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving path:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save update path', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleOpenDeleteDialog = (item, type) => {
    setDeleteTarget({ item, type });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    try {
      if (deleteTarget.type === 'firmware') {
        await firmwareVersionsApi.delete(deleteTarget.item.id);
        enqueueSnackbar('Firmware version deleted', { variant: 'success' });
      } else {
        await updatePathsApi.delete(deleteTarget.item.id);
        enqueueSnackbar('Update path deleted', { variant: 'success' });
      }

      handleCloseDeleteDialog();
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to delete', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Permission check
  if (!canManage) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You do not have permission to manage firmware. Admin or device edit permissions are required.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FirmwareIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Firmware Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage firmware versions and update paths for devices
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Device Type Filter */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Filter by Device Type</InputLabel>
          <Select
            value={selectedDeviceType || ''}
            onChange={(e) => setSelectedDeviceType(e.target.value || null)}
            label="Filter by Device Type"
          >
            <MenuItem value="">All Device Types</MenuItem>
            {deviceTypes.map((dt) => (
              <MenuItem key={dt.id} value={dt.id}>
                {dt.friendlyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedDeviceType && getDeviceScriptFolder(deviceTypes.find(dt => dt.id === selectedDeviceType)) && (
          <Button
            variant="outlined"
            startIcon={<DocIcon />}
            onClick={handleOpenDocDialog}
          >
            View Documentation
          </Button>
        )}
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {filteredFirmware.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Firmware Versions
                  </Typography>
                </Box>
                <FirmwareIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {filteredPaths.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update Paths
                  </Typography>
                </Box>
                <PathIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {deviceTypes.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Device Types
                  </Typography>
                </Box>
                <DeviceIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Firmware Versions" icon={<FirmwareIcon />} iconPosition="start" />
          <Tab label="Update Paths" icon={<PathIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Firmware Versions Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenFirmwareDialog()}
          >
            Add Firmware Version
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Device Type</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFirmware.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No firmware versions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFirmware.map((fw) => (
                  <TableRow key={fw.id} hover>
                    <TableCell>{fw.id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getDeviceTypeName(fw.device_type)}
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        v{fw.version}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={fw.path}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {fw.path}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {fw.isLatest && (
                        <Chip
                          size="small"
                          icon={<LatestIcon />}
                          label="Latest"
                          color="success"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {fw.created_at ? new Date(fw.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenFirmwareDialog(fw)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(fw, 'firmware')}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Update Paths Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            <AlertTitle>About Update Paths</AlertTitle>
            Update paths define the firmware upgrade chain for each device type.
            Each record specifies which version a device should upgrade to.
            <strong> A "To Version" of 0 means the device is on the latest version.</strong>
          </Alert>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPathDialog()}
          >
            Add Update Path
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Device Type</TableCell>
                <TableCell>From Version</TableCell>
                <TableCell></TableCell>
                <TableCell>To Version</TableCell>
                <TableCell>Can Skip</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPaths.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No update paths found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPaths.map((path) => (
                  <TableRow key={path.id} hover>
                    <TableCell>{path.id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getDeviceTypeName(path.update_path_device_type)}
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`v${path.actual_version}`}
                        color="warning"
                      />
                    </TableCell>
                    <TableCell>
                      <ArrowIcon color="action" />
                    </TableCell>
                    <TableCell>
                      {path.next_version === 0 ? (
                        <Chip
                          size="small"
                          icon={<LatestIcon />}
                          label="Latest"
                          color="success"
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={`v${path.next_version}`}
                          color="info"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {path.canSkip ? (
                        <Chip size="small" label="Yes" color="info" />
                      ) : (
                        <Chip size="small" label="No" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {path.created_at ? new Date(path.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPathDialog(path)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(path, 'path')}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Firmware Dialog */}
      <Dialog open={firmwareDialogOpen} onClose={handleCloseFirmwareDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFirmware ? 'Edit Firmware Version' : 'Add Firmware Version'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={firmwareForm.device_type}
                onChange={(e) => setFirmwareForm({ ...firmwareForm, device_type: e.target.value })}
                label="Device Type"
              >
                {deviceTypes.map((dt) => (
                  <MenuItem key={dt.id} value={dt.id}>
                    {dt.friendlyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Version Number"
              type="number"
              required
              value={firmwareForm.version}
              onChange={(e) => setFirmwareForm({ ...firmwareForm, version: e.target.value })}
              helperText="Numeric version identifier (e.g., 1, 2, 3)"
            />

            <TextField
              label="Script/File Path"
              required
              value={firmwareForm.path}
              onChange={(e) => setFirmwareForm({ ...firmwareForm, path: e.target.value })}
              helperText="Path to the firmware script (e.g., caro_hub/v1)"
            />

            <TextField
              label="Download URL"
              value={firmwareForm.downloadUrl}
              onChange={(e) => setFirmwareForm({ ...firmwareForm, downloadUrl: e.target.value })}
              helperText="URL where the firmware can be downloaded from"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={firmwareForm.isLatest}
                  onChange={(e) => setFirmwareForm({ ...firmwareForm, isLatest: e.target.checked })}
                />
              }
              label="Mark as Latest Version"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFirmwareDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFirmware}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : editingFirmware ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Path Dialog */}
      <Dialog open={pathDialogOpen} onClose={handleClosePathDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPath ? 'Edit Update Path' : 'Add Update Path'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={pathForm.update_path_device_type}
                onChange={(e) => setPathForm({ ...pathForm, update_path_device_type: e.target.value })}
                label="Device Type"
              >
                {deviceTypes.map((dt) => (
                  <MenuItem key={dt.id} value={dt.id}>
                    {dt.friendlyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="New Version Number"
              type="number"
              required
              value={pathForm.actual_version}
              onChange={(e) => setPathForm({ ...pathForm, actual_version: e.target.value })}
              helperText="The new firmware version number to add (e.g., 3)"
              inputProps={{ min: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={pathForm.canSkip}
                  onChange={(e) => setPathForm({ ...pathForm, canSkip: e.target.checked })}
                />
              }
              label="Can Skip This Update"
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              <AlertTitle>Automatic Update Chain</AlertTitle>
              <Typography variant="body2">
                When you add a new version, the system will automatically:
                <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Find the current latest version for this device type</li>
                  <li>Update it to point to this new version</li>
                  <li>Mark this new version as the latest</li>
                </ol>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePathDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePath}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : editingPath ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteTarget?.type === 'firmware' ? 'firmware version' : 'update path'}?
          </Typography>
          {deleteTarget?.type === 'firmware' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Deleting a firmware version may break existing update paths that reference it.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documentation Dialog */}
      <Dialog
        open={docDialogOpen}
        onClose={() => setDocDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DocIcon color="primary" />
          Documentation: {docTitle}
        </DialogTitle>
        <DialogContent dividers>
          {docLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {docError && !docContent && (
            <Alert severity="info" sx={{ my: 2 }}>
              {docError}
            </Alert>
          )}
          {!docLoading && docContent && (
            <Box
              sx={{
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  mt: 3,
                  mb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  pb: 1
                },
                '& h2': {
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mt: 2.5,
                  mb: 1.5
                },
                '& h3': {
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  mt: 2,
                  mb: 1
                },
                '& p': {
                  mb: 2,
                  lineHeight: 1.7
                },
                '& code': {
                  backgroundColor: 'action.hover',
                  padding: '2px 6px',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace'
                },
                '& pre': {
                  backgroundColor: 'action.hover',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  mb: 2,
                  '& code': {
                    backgroundColor: 'transparent',
                    padding: 0
                  }
                },
                '& ul, & ol': {
                  mb: 2,
                  pl: 3
                },
                '& li': {
                  mb: 0.5
                },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary'
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2
                },
                '& th': {
                  backgroundColor: 'action.hover',
                  fontWeight: 'bold',
                  padding: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                },
                '& td': {
                  padding: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  mt: 2,
                  mb: 2
                }
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
              >
                {docContent}
              </ReactMarkdown>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FirmwareManagement;
